import {Grid, Tooltip, Typography} from '@mui/material'
import {Box} from '@viptech/react-components'
import {useEffect, useState} from 'react'
import {useCompany} from 'src/contexts/CompanyContext'
import useDashboardAgeAndGender from 'src/hooks/api/dashboard/useDashboardAgeAndGender'
import useDashboardCountingObjects from 'src/hooks/api/dashboard/useDashboardCountingObjects'
import useDashboardFacialRecognition from 'src/hooks/api/dashboard/useDashboardFacialRecognition'
import useDashboardGetAllCountingObjects from 'src/hooks/api/dashboard/useDashboardGetAllCountingObjects'
import useDashboardHeatMap from 'src/hooks/api/dashboard/useDashboardHeatMap'
import useDashboardLpr from 'src/hooks/api/dashboard/useDashboardLpr'
import useDashboardObjectsCount from 'src/hooks/api/dashboard/useDashboardObjectsCount'
import useDashboardPermanencyAvg from 'src/hooks/api/dashboard/useDashboardPermanencyAvg'
import useDashboardSeriesByFace from 'src/hooks/api/dashboard/useDashboardSeriesByFace'
import useDashboardSeriesByGender from 'src/hooks/api/dashboard/useDashboardSeriesByGender'
import useDashboardSeriesByObjects from 'src/hooks/api/dashboard/useDashboardSeriesByObject'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import AgeIcon from 'src/images/AgeIcon'
import EpiDetectedIcon from 'src/images/EpiDetectedIcon'
import FaceDetectedIcon from 'src/images/FaceDetectedIcon'
import InfoIcon from 'src/images/infoIcon.svg'
import MenIcon from 'src/images/MenIcon'
import PermanencyDetectedIcon from 'src/images/PermanencyDetectedIcon'
import PersonDetectedIcon from 'src/images/PersonDetectedIcon'
import WomenIcon from 'src/images/WomenIcon'
import {objectTypeList} from 'src/pages/alarms/AlarmsFunctions'
import {ContainerCards} from 'src/pages/dashboard/components/DetectionsDashboardBase'
import {
  AGE_GROUPS,
  calculatePercent,
  DEFAULT_COLORS,
  GenderEnum,
  getAgeGroup,
  getColorByGender,
  getFaceDetectionColor,
  translateGender,
  translateObjectName,
} from 'src/pages/dashboard/default-dashboard/functions'
import useCameras from '../../../hooks/api/camera/useCameras'
import DashboardColumnChartsCard from '../components/ColumnChartCard'
import DashboardCountCard from '../components/CountCard'
import DashboardDonutChartCard from '../components/DonutChartsCard'
import HeatMapCard from '../components/HeatMapCard'
import DashboardLineChart from '../components/LineChart'
import DashboardLprCard from '../components/LprCard'
import DashboardStackChart from '../components/StackChart'
import DashboardTableRowCard from '../components/TableRowCard'

type Props = {
  filter: {cameraIds: Array<number>; clientIds: Array<number>; start: string; end: string}
  isFacialEnabled: boolean
  isLprEnabled: boolean
  isCountingObjectsEnable: boolean
  isHeatmapEnable: boolean
}

export default function DetectionsDashboard({filter, isFacialEnabled, isLprEnabled, isCountingObjectsEnable, isHeatmapEnable}: Props) {
  const {selectedCompanyId} = useCompany()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const {
    data: lprData,
    isRefetching: isRefetchingLprData,
    isLoading: isLoadingLprData,
  } = useDashboardLpr({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isLprEnabled,
  })
  const {
    data: objectsCountData,
    isRefetching: isRefetchingObjectsCountData,
    isLoading: isLoadingObjectsCountData,
  } = useDashboardObjectsCount({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: selectedCompanyId !== 0,
  })
  const {
    data: ageAndGenderData,
    isRefetching: isRefetchingAgeAndGenderData,
    isLoading: isLoadingAgeAndGenderData,
  } = useDashboardAgeAndGender({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isFacialEnabled,
  })

  const {
    data: facialRecognitionData,
    isRefetching: isRefetchingFacialRecognitionData,
    isLoading: isLoadingFacialRecognitionData,
  } = useDashboardFacialRecognition({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isFacialEnabled,
  })
  const {
    data: seriesByGenderData,
    isRefetching: isRefetchingSeriesByGenderData,
    isLoading: isLoadingSeriesByGenderData,
  } = useDashboardSeriesByGender({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isFacialEnabled,
  })
  const {
    data: seriesByObjectData,
    isRefetching: isRefetchingSeriesByObjectData,
    isLoading: isLoadingSeriesByObjectData,
  } = useDashboardSeriesByObjects({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: selectedCompanyId !== 0,
  })
  const {
    data: seriesByFaceData,
    isRefetching: isRefetchingSeriesByFaceData,
    isLoading: isLoadingSeriesByFaceData,
  } = useDashboardSeriesByFace({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isFacialEnabled,
  })

  const {
    data: countingObjectsData,
    isRefetching: isRefetchingCountingObjectData,
    isLoading: isLoadingCountingObjectData,
  } = useDashboardCountingObjects({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isCountingObjectsEnable,
  })

  const {
    data: permanencyAverageData,
    isRefetching: isRefetchingPermanencyAvg,
    isLoading: isLoadingPermanencyAvg,
  } = useDashboardPermanencyAvg({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: selectedCompanyId !== 0,
  })

  const {
    data: totalCountingObjectsByLabel,
    isRefetching: totalCountingObjectsIsRefetching,
    isLoading: totalCountingObjectsIsLoading,
  } = useDashboardGetAllCountingObjects({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    enabled: isCountingObjectsEnable,
  })

  const {data: cameras} = useCameras({selectedCompanies: [selectedCompanyId], enabled: selectedCompanyId !== 0})
  const [camerasIds, setCamerasIds] = useState(cameras?.data.data.entities.map((it) => it.id))

  useEffect(() => {
    if (cameras?.data.data.entities.length) setCamerasIds(cameras.data.data.entities.map((it) => it.id))
  }, [cameras])

  const {data: heatmapDatas} = useDashboardHeatMap({
    cameras: filter.cameraIds.length ? filter.cameraIds : (camerasIds ?? []),
    start: filter.start,
    end: filter.end,
    enabled: isHeatmapEnable,
  })

  function findCameraName(cameraId: number): string {
    return cameras?.data.data.entities.find((cam) => cam.id === cameraId)?.name ?? 'Câmera'
  }

  function sumWithoutEpiValue() {
    const epiOptions = objectTypeList.filter((obj) => obj.resourceId === 9).map((it) => it.name)
    let sum = 0
    epiOptions.forEach((epi) => {
      if (objectsCountData?.[epi]) sum += objectsCountData?.[epi]
    })
    return sum
  }

  function handleTimeForPermanency(timeInSeconds: number) {
    const hours = Math.floor(timeInSeconds / 3600)
    const minutes = Math.floor((timeInSeconds % 3600) / 60)
    const seconds = timeInSeconds

    const parts = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)

    return parts.join(' ')
  }

  return (
    <Grid container gap={'40px'} padding={'0 20px'}>
      {/* row */}
      <Grid item container xs={12} spacing={'32px'}>
        <ContainerCards>
          {objectsCountData?.['person'] && (
            <DashboardCountCard
              title={'Pessoas Detectadas'}
              description='pessoas'
              value={objectsCountData?.['person']}
              loading={isLoadingObjectsCountData}
              refetching={isRefetchingObjectsCountData}
              icon={<PersonDetectedIcon />}
            />
          )}

          {isFacialEnabled && (
            <>
              <DashboardCountCard
                title={'Faces Detectadas'}
                description='faces'
                value={facialRecognitionData?.detections}
                loading={isLoadingFacialRecognitionData}
                refetching={isRefetchingFacialRecognitionData}
                icon={<FaceDetectedIcon />}
              />

              <DashboardCountCard
                title={'Faces Reconhecidas'}
                description={` / ${facialRecognitionData?.detections ?? 0} faces reconhecidas`}
                value={facialRecognitionData?.recognizedFaces}
                loading={isLoadingFacialRecognitionData}
                refetching={isRefetchingFacialRecognitionData}
                icon={<FaceDetectedIcon />}
              />
            </>
          )}
          <DashboardCountCard
            title={'Pessoas sem EPI'}
            description='pessoas sem EPI'
            value={sumWithoutEpiValue()}
            loading={isLoadingObjectsCountData}
            refetching={isRefetchingObjectsCountData}
            icon={<EpiDetectedIcon />}
          />
          {permanencyAverageData &&
            permanencyAverageData.objects.length > 0 &&
            permanencyAverageData.objects.map((camera) => {
              return (
                <DashboardCountCard
                  title={'Tempo de Permanência'}
                  description={`em média na câmera ${camera.cameraName}`}
                  value={handleTimeForPermanency(Number(Number(camera.time).toFixed(0)))}
                  loading={isLoadingPermanencyAvg}
                  refetching={isRefetchingPermanencyAvg}
                  icon={<PermanencyDetectedIcon />}
                />
              )
            })}
        </ContainerCards>
      </Grid>

      {/* row */}
      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={12} lg={isFacialEnabled || isLprEnabled ? 6 : 12}>
          <DashboardColumnChartsCard
            title='Visão geral'
            loading={isLoadingObjectsCountData}
            refetching={isRefetchingObjectsCountData}
            data={[{name: '', data: Object.values(objectsCountData ?? {})}]}
            labelsXaxis={Object.keys(objectsCountData ?? {}).map((key) => translateObjectName(key))}
          />
        </Grid>
        {isFacialEnabled && (
          <Grid item container xs={isLprEnabled ? 6 : 12} lg={isLprEnabled ? 3 : 6}>
            <DashboardDonutChartCard
              title={'Faces detectadas'}
              total={facialRecognitionData?.detections}
              loading={isLoadingFacialRecognitionData}
              refetching={isRefetchingFacialRecognitionData}
              data={[facialRecognitionData?.unkownFaces ?? 0, facialRecognitionData?.allowFaces ?? 0, facialRecognitionData?.notAllowFaces ?? 0]}
              labelsXaxis={['Desconhecidas', 'Permitidas', 'Bloqueadas']}
              colors={['#D1E9FF', '#0D9800', '#D71717']}
              labelOnLegend='faces'
            />
          </Grid>
        )}
        {isLprEnabled && (
          <Grid item container xs={isFacialEnabled ? 6 : 12} lg={isFacialEnabled ? 3 : 6}>
            <DashboardLprCard
              title={'Detecção de placas (LPR)'}
              loading={isLoadingLprData}
              refetching={isRefetchingLprData}
              data={[
                {color: '#0D9800', name: 'Permitidas', value: lprData?.allowPlates ?? 0},
                {color: '#D71717', name: 'Bloqueadas', value: lprData?.notAllowPlates ?? 0},
              ]}
              description='placas detectadas'
              total={lprData?.detections ?? 0}
            />
          </Grid>
        )}
      </Grid>

      {/* row */}
      {isFacialEnabled && (
        <Grid item container xs={12} spacing={'32px'}>
          <Grid item container xs={6} lg={4}>
            <DashboardTableRowCard
              title='Visão Geral de Detecções'
              loading={isLoadingAgeAndGenderData}
              refetching={isRefetchingAgeAndGenderData}
              data={[
                {
                  name: 'Homens',
                  icon: <MenIcon />,
                  value: ageAndGenderData?.totalMale,
                  percentage: calculatePercent(ageAndGenderData?.totalMale ?? 0, (ageAndGenderData?.totalFemale ?? 0) + (ageAndGenderData?.totalMale ?? 0)),
                },
                {
                  name: 'Mulheres',
                  icon: <WomenIcon />,
                  value: ageAndGenderData?.totalFemale,
                  percentage: calculatePercent(ageAndGenderData?.totalFemale ?? 0, (ageAndGenderData?.totalFemale ?? 0) + (ageAndGenderData?.totalMale ?? 0)),
                },
                {name: 'Média de idade', icon: <AgeIcon />, value: Number(ageAndGenderData?.avarageAge?.toFixed(2))},
              ]}
            />
          </Grid>
          <Grid item container xs={6} lg={4}>
            <DashboardColumnChartsCard
              title='Faixa Etária'
              loading={isLoadingAgeAndGenderData}
              refetching={isRefetchingAgeAndGenderData}
              data={[
                {
                  name: 'Homens',
                  data: getAgeGroup(
                    ageAndGenderData?.categories,
                    ageAndGenderData?.series?.find((s) => s.name === GenderEnum.MALE),
                  ),
                  color: '#0B62B9',
                },
                {
                  name: 'Mulheres',
                  data: getAgeGroup(
                    ageAndGenderData?.categories,
                    ageAndGenderData?.series?.find((s) => s.name === GenderEnum.WOMAN),
                  ),
                  color: '#9539F2',
                },
                {
                  name: 'Indeterminado',
                  data: getAgeGroup(
                    ageAndGenderData?.categories,
                    ageAndGenderData?.series?.find((s) => s.name === GenderEnum.UNDETERMINED),
                  ),
                  color: '#FDB022',
                },
              ]}
              labelsXaxis={AGE_GROUPS}
            />
          </Grid>
          <Grid item container xs={12} lg={4}>
            <DashboardColumnChartsCard
              title='Idade'
              loading={isLoadingAgeAndGenderData}
              refetching={isRefetchingAgeAndGenderData}
              data={
                ageAndGenderData?.series?.map((data) => ({
                  data: data.data,
                  name: translateGender(data.name),
                  color: getColorByGender(data.name),
                })) ?? []
              }
              labelsXaxis={ageAndGenderData?.categories}
            />
          </Grid>
        </Grid>
      )}

      {/* row */}
      <Grid item container xs={12} spacing={'32px'}>
        {isFacialEnabled && (
          <>
            <Grid item container xs={12} lg={6}>
              <DashboardStackChart
                title='Registros por Gênero'
                data={
                  seriesByGenderData?.result?.map((data) => ({data: data.data, name: translateGender(data.name), color: getColorByGender(data.name)})) ?? []
                }
                isLoading={isLoadingSeriesByGenderData}
                isRefetching={isRefetchingSeriesByGenderData}
                labelsXaxis={seriesByGenderData?.categories}
              />
            </Grid>
            <Grid item container xs={12} lg={6}>
              <DashboardStackChart
                title='Registros por Faces'
                data={seriesByFaceData?.result?.map((data) => ({data: data.data, name: data.name, color: getFaceDetectionColor(data.name)})) ?? []}
                isLoading={isLoadingSeriesByFaceData}
                isRefetching={isRefetchingSeriesByFaceData}
                labelsXaxis={seriesByFaceData?.categories}
              />
            </Grid>
          </>
        )}

        <Grid item container xs={12} lg={12}>
          <DashboardStackChart
            title='Registros por Objetos'
            data={
              Object.entries(seriesByObjectData?.data ?? []).map(([key, value], index) => ({
                data: value,
                name: translateObjectName(key),
                color: DEFAULT_COLORS[index],
              })) ?? []
            }
            isLoading={isLoadingSeriesByObjectData}
            isRefetching={isRefetchingSeriesByObjectData}
            labelsXaxis={seriesByObjectData?.categories}
          />
        </Grid>

        {isCountingObjectsEnable && totalCountingObjectsByLabel && (
          <Grid item container xs={12} spacing={'32px'}>
            {totalCountingObjectsByLabel.map((it) => {
              return (
                <Grid item container xs={6} lg={6}>
                  <DashboardDonutChartCard
                    title={`Contagem de ${it[0]}s`}
                    total={it[1].total}
                    loading={totalCountingObjectsIsLoading}
                    refetching={totalCountingObjectsIsRefetching}
                    data={it[1].count}
                    labelsXaxis={it[1].cameras}
                    labelOnLegend={it[0]}
                  />
                </Grid>
              )
            })}

            <Grid item container xs={6} lg={6}>
              <DashboardLineChart
                title='Contagem de Objetos por hora'
                data={
                  Object.entries(countingObjectsData?.data ?? []).map(([key, value], index) => ({
                    data: value,
                    name: key,
                    color: DEFAULT_COLORS[index],
                  })) ?? []
                }
                isLoading={isLoadingCountingObjectData}
                isRefetching={isRefetchingCountingObjectData}
                labelsXaxis={countingObjectsData?.categories}
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      {isHeatmapEnable && (
        <Grid item container xs={12} spacing={'32px'}>
          <Grid item container xs={12} lg={12}>
            <Box
              display='flex'
              direction='column'
              align='center'
              backgroundColor='white'
              borderRadius='16px'
              width='100%'
              rowGap='20px'
              padding='20px'
              style={{boxShadow: '0px 5px 8px 0px #00000026'}}>
              <Box display='flex' width='100%' align='center' justify='center' columnGap='10px'>
                <Typography
                  fontWeight={700}
                  maxWidth='100%'
                  fontSize='18px'
                  textAlign='center'
                  color={styledTheme.dashboard.colors.title}
                  children='Mapa de Calor'
                />
                <Tooltip title='Exibindo no máximo uma câmera. Para ver mais, aplique um filtro de câmeras'>
                  <img src={InfoIcon} alt='info' />
                </Tooltip>
              </Box>
              <Box display='grid' style={{gridTemplateColumns: 'repeat(2,1fr)', gap: '20px'}} columnGap='20px' width='100%'>
                {heatmapDatas?.map((it, idx) => {
                  return <HeatMapCard key={idx} title={`${findCameraName(it.cameraId)} - ${translateObjectName(it.objectName)}`} data={it}></HeatMapCard>
                })}
              </Box>
              {!heatmapDatas?.length && (
                <Box width='100%' display='flex' align='center' justify='center' height='200px'>
                  <Typography color='#8E8E8E' fontSize='16px' children='Nenhum dado foi encontrado' />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}
